import React, { useState, useEffect } from "react";
import { Spin } from "antd";
import PastTable from "./PastTable/PastTable";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getURL, setNavItem,getAccountId, getToken } from "../../../utils/localStorage";
import { FilledButton } from "../../commonComponents";
import OngoingTable from "./OngoingTable/OngoingTable";
import PrivateGamesTable from "./PrivateGames/PrivateGames";
import { useScreenWidth } from "../../../utils/useScreenWidth";
import { useMyContext } from "../../../helpers/Context/MyContext";
import { isApiError } from "../../../helpers/genericFunctions";
import { PRIVATE_GAMES_ENABLED } from "../../../helpers/privateGames";
import {
  getPrivateGameByLink,
  getPrivateGames,
  getPastGames,
  getOngoingGames,
} from "../../../utils/service";

import "./Dashboard.scss";

const Dashboard = () => {
  const [privateGameTableData, setPrivateGameTableData] = useState();
  const [pastTableData, setPastTableData] = useState([]);
  const [ongoingTableData, setOngoingTableData] = useState();
  const [loading, setLoading] = useState(true);
  // Tracked per section: a failed request must not render as an empty one.
  const [ongoingFailed, setOngoingFailed] = useState(false);
  const [pastFailed, setPastFailed] = useState(false);
  const [privateFailed, setPrivateFailed] = useState(false);
  const screenWidth = useScreenWidth(true);
  const privatelink = getURL();
  const [searchParams] = useSearchParams();
  const private_game_token = searchParams.get("private_token");
  const { contextData, setContextData } = useMyContext();
  const navigate = useNavigate();
 const token = getToken()
  const playerId = getAccountId()
  const handlePrivateGames = async () => {
    // Out of scope for BetArabia: nothing renders the result, and
    // getPrivateGameByLink creates a PrivateGame row on the backend as a side
    // effect of being called, so skip the request entirely rather than
    // fetching and discarding.
    if (!PRIVATE_GAMES_ENABLED) return;
    setPrivateFailed(false);
    const response = await getPrivateGames(playerId, token);
    if (private_game_token) {
      const resp = await getPrivateGameByLink(
        private_game_token,
        playerId,
        token
      );
      if (isApiError(response) || isApiError(resp)) {
        setPrivateFailed(true);
        return;
      }
      // Guarded: these spreads threw a TypeError whenever either request
      // failed, because a failed call returns no `game` array.
      const combinedArray = [...(response?.game || []), ...(resp?.game || [])];
      const modifiedGameData = combinedArray?.map((game) => ({
        ...game,
        key: game.id,
      }));
      setPrivateGameTableData(modifiedGameData);
    } else {
      if (isApiError(response)) {
        setPrivateFailed(true);
        return;
      }
      if (response) {
        const modifiedGameData = response?.game?.map((game) => ({
          ...game,
          key: game.id,
        }));
        setPrivateGameTableData(modifiedGameData);
      }
    }
  };
  const handlePastGamesResponse = async () => {
    setPastFailed(false);
    const response = await getPastGames(playerId, token);
    if (isApiError(response)) {
      setPastFailed(true);
      return;
    }
    if (response?.success) {
      setPastTableData(response?.past_games);
    }
  };
  const handleOngoingResponse = async () => {
    setLoading(true);
    setOngoingFailed(false);
    setOngoingTableData([]);
    const response = await getOngoingGames(playerId, token);
    // Outside the `if` below: previously a request that returned nothing left
    // the spinner up forever instead of resolving to any end state.
    setLoading(false);
    if (isApiError(response)) {
      setOngoingFailed(true);
      return;
    }
    if (response) {
      const modifiedGameData = response?.data?.map((game) => ({
        ...game,
        key: game?.id,
      }));
      setOngoingTableData(modifiedGameData);
      setContextData((prevState) => ({
        ...prevState,
        onGoingGames: response?.data?.length,
      }));
    }
  };
  useEffect(() => {
    if (privatelink) {
      const sectionElement = document.getElementById("targetSectionId");
      window.scrollTo({
        top: sectionElement?.offsetTop,
        behavior: "smooth",
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [privatelink]);
  useEffect(() => {
    handlePrivateGames();
    handlePastGamesResponse();
    handleOngoingResponse();
    setNavItem(3);
  }, []);
  return (
    <div className="white-color">
      <div className="dashboard">
        <div className="dashboard-title font-cairo-extrabold">
          Champion's Dashboard
        </div>
        <div className="dashboard-ongoing">
          <div className="dashboard-ongoing-title font-cairo-extrabold">
            Ongoing Games
          </div>
          {ongoingTableData?.length > 0 ? (
            <div
              className={`${
                screenWidth >= 767 && "dashboard-ongoing-cont"
              } background-blur`}
            >
              <OngoingTable
                ongoingTableData={ongoingTableData}
                getOngoingGames={handleOngoingResponse}
              />
            </div>
          ) : loading ? (
            <div className="spinner-loading font-cairo-medium">
              <Spin className="loader" />
            </div>
          ) : ongoingFailed ? (
            <div className="dashboard-ongoing-nodata font-cairo-medium">
              We couldn’t load your games right now. Please refresh the page to
              try again.
            </div>
          ) : (
            <div className="dashboard-ongoing-nodata font-cairo-medium">
              You currently have no active games. Ready for another round? Dive
              into the excitement by exploring our range of games using the link
              below and embark on your next Champion’s journey!
            </div>
          )}
        </div>
        {PRIVATE_GAMES_ENABLED && privateFailed && (
          <div className="dashboard-ongoing">
            <div className="dashboard-ongoing-nodata font-cairo-medium">
              We couldn’t load your private games right now.
            </div>
          </div>
        )}
        {PRIVATE_GAMES_ENABLED && privateGameTableData?.length > 0 && (
          <div className="dashboard-ongoing">
            <div
              className="dashboard-past-title font-cairo-extrabold"
              id="targetSectionId"
            >
              Private Games
            </div>
            <div
              className={`${
                screenWidth >= 767 && "dashboard-ongoing-cont"
              } background-blur`}
            >
              <PrivateGamesTable privateGameTableData={privateGameTableData} />
            </div>
          </div>
        )}
        {pastFailed && (
          <div className="dashboard-past">
            <div className="dashboard-ongoing-nodata font-cairo-medium">
              We couldn’t load your past games right now.
            </div>
          </div>
        )}
        {pastTableData?.length > 0 && (
          <div className="dashboard-past">
            {/* Reuses dashboard-ongoing-title (not dashboard-past-title, which
                is 32px vs this one's 20px) so "Past Games" and "Ongoing Games"
                render at the same size instead of duplicating the font-size/
                line-height values here. dashboard-past-title is left in place
                for the separate "Private Games" heading above, which still
                uses it. */}
            <div className="dashboard-ongoing-title font-cairo-extrabold">
              Past Games
            </div>
            <div
              className={`${
                screenWidth >= 767 && "dashboard-past-cont"
              } background-blur`}
            >
              <PastTable pastTableData={pastTableData} />
            </div>
          </div>
        )}
        <div className="dashboard-button">
          <FilledButton
            name={"Explore Other Games"}
            btnClass="primary-background"
            type={1}
            functionCall={() => {
              setNavItem(1);
              navigate("/game");
            }}
          />
        </div>
        <div className="dashboard-past"></div>
      </div>
    </div>
  );
};

export default Dashboard;
